import React from 'react'
import styled from 'styled-components'
import { STYLES } from '../../static/style'
import { ButtonType } from './ButtonType.constants'
import { ClipSpinner } from '../Spinner/ClipSpinner'

enum ButtonSize {
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
}

enum ButtonThemeType {
  WARNING = 'WARNING',
  PRIMARY = 'PRIMARY',
  GRAY = 'GRAY',
  BLUE = 'BLUE',
  RED = 'RED',
  NORMAL = 'NORMAL',
}

const BUTTON_THEME = {
  WARNING: {
    backgroundColor: STYLES.COLOR_VIVID_RED,
    color: STYLES.COLOR_WHITE,
    borderColor: STYLES.COLOR_VIVID_RED,
    backgroundImage: STYLES.COLOR_BITTER_SWEET_RED,
    hoverBorderColor: STYLES.COLOR_BITTER_SWEET_RED,
    hoverBackgroundColor: STYLES.COLOR_BITTER_SWEET_RED,
    hoverBackgroundImage: STYLES.COLOR_BITTER_SWEET_RED,
  },
  PRIMARY: {
    backgroundColor: STYLES.BUTTON_PRIMARY_COLOR,
    color: STYLES.BUTTON_PRIMARY_TEXT_COLOR,
    fontWeight: STYLES.FONT_WEIGHT_BOLD,
    borderColor: STYLES.BUTTON_PRIMARY_BORDER_COLOR,
    backgroundImage: STYLES.BUTTON_PRIMARY_BACKGROUND_IMAGE,
    hoverBorderColor: STYLES.BUTTON_PRIMARY_BORDER_HOVER_COLOR,
    hoverBackgroundColor: STYLES.BUTTON_PRIMARY_HOVER_COLOR,
    hoverBackgroundImage: STYLES.BUTTON_PRIMARY_HOVER_BACKGROUND_IMAGE,
  },
  GRAY: {
    backgroundColor: STYLES.BUTTON_GRAY_COLOR,
    color: STYLES.COLOR_WHITE,
    borderColor: STYLES.BUTTON_GRAY_COLOR,
    backgroundImage: STYLES.BUTTON_GRAY_BACKGROUND_IMAGE,
    hoverBorderColor: STYLES.BUTTON_GRAY_BORDER_HOVER_COLOR,
    hoverBackgroundColor: STYLES.BUTTON_GRAY_HOVER_COLOR,
    hoverBackgroundImage: STYLES.BUTTON_GRAY_HOVER_BACKGROUND_IMAGE,
  },
  BLUE: {
    backgroundColor: STYLES.BUTTON_BLUE_COLOR,
    color: STYLES.COLOR_WHITE,
    borderColor: STYLES.BUTTON_BLUE_COLOR,
    backgroundImage: STYLES.BUTTON_BLUE_BACKGROUND_IMAGE,
    hoverBorderColor: STYLES.BUTTON_BLUE_BORDER_HOVER_COLOR,
    hoverBackgroundColor: STYLES.BUTTON_BLUE_HOVER_COLOR,
    hoverBackgroundImage: STYLES.BUTTON_BLUE_HOVER_BACKGROUND_IMAGE,
  },
  RED: {
    backgroundColor: STYLES.BUTTON_RED_COLOR,
    color: STYLES.COLOR_WHITE,
    borderColor: STYLES.BUTTON_RED_COLOR,
    backgroundImage: STYLES.BUTTON_RED_BACKGROUND_IMAGE,
    hoverBorderColor: STYLES.BUTTON_RED_BORDER_HOVER_COLOR,
    hoverBackgroundColor: STYLES.BUTTON_RED_HOVER_COLOR,
    hoverBackgroundImage: STYLES.BUTTON_RED_HOVER_BACKGROUND_IMAGE,
  },
  NORMAL: {
    backgroundColor: STYLES.BUTTON_SECONDARY_COLOR,
    color: STYLES.TEXT_COLOR,
    borderColor: STYLES.BUTTON_SECONDARY_BORDER_COLOR,
    backgroundImage: STYLES.BUTTON_SECONDARY_BACKGROUND_IMAGE,
    hoverBorderColor: STYLES.BUTTON_SECONDARY_BORDER_HOVER_COLOR,
    hoverBackgroundColor: STYLES.BUTTON_SECONDARY_HOVER_COLOR,
    hoverBackgroundImage: STYLES.BUTTON_SECONDARY_HOVER_BACKGROUND_IMAGE,
  },
}

const StyledButton = styled.button`
  border-radius: ${STYLES.BORDER_RADIUS};
  border: 1px solid;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  &:focus {
    outline: 0;
  }
  cursor: pointer;
  ${({ theme, disabled, size, inline }) =>
    `
      background-color: ${theme.backgroundColor};
      color: ${theme.color};
      border-color: ${theme.borderColor};
      font-weight: ${theme.fontWeight || STYLES.FONT_WEIGHT_NORMAL};
      ${
        inline
          ? `
        display: inline-block;
        width: auto;
      `
          : `
        display: block;
        width: 100%;
      `
      }
      ${
        theme.backgroundImage &&
        `
        background-image: ${theme.backgroundImage};
      `
      }
      ${
        !disabled &&
        `
        &:hover {
          border-color: ${theme.hoverBorderColor};
          background-color: ${theme.hoverBackgroundColor};
          ${
            theme.hoverBackgroundImage &&
            `
            background-image: ${theme.hoverBackgroundImage};
          `
          }
        }
      `
      }
      ${
        size &&
        `
          font-size: ${STYLES[`BUTTON_FONT_SIZE_${size}`]};
          padding: ${STYLES[`BUTTON_PADDING_${size}`]};
        `
      }
    `}
`

const ButtonContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const ButtonContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ $loading }: { $loading: boolean }) =>
    $loading &&
    `
    margin-left: calc(${STYLES.GUTTER} / 2);
  `}
`

export interface IButtonProps {
  className?: string
  children?: React.ReactNode
  disabled?: boolean
  size?: ButtonSize
  title?: string
  inline?: boolean
  onClick?: () => void
  loading?: boolean
  type?: ButtonType
  theme?: ButtonThemeType
  onMouseOver?: () => void
  onMouseLeave?: () => void
  label: string
}

const Button: React.FC<IButtonProps> = ({
  className,
  children,
  theme = ButtonThemeType.NORMAL,
  size = ButtonSize.MD,
  inline = false,
  disabled = false,
  onMouseOver,
  onMouseLeave,
  onClick,
  type = ButtonType.BUTTON,
  loading = false,
  title,
  label,
}) => {
  return (
    <StyledButton
      type={type}
      title={disabled ? 'Disabled' : title}
      className={className}
      size={size}
      theme={BUTTON_THEME[theme]}
      inline={inline}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      aria-label={label}
    >
      <ButtonContentContainer>
        {loading && <ClipSpinner />}
        <ButtonContent $loading={loading}>{children}</ButtonContent>
      </ButtonContentContainer>
    </StyledButton>
  )
}

export { Button, ButtonSize, ButtonThemeType }
